// =====================================================================
// MI CUENTA → SEGURIDAD → LLAVES DE ACCESO · DIRECTRIZ-LLAVE L-3
// =====================================================================
// Lo que la persona ve de sus llaves: cuáles tiene, de qué aparato, desde
// cuándo, cuándo se usó cada una, y los dos actos que cambian quién puede
// entrar —agregar y quitar—, los dos detrás de «Confirma que eres tú».
//
// LA PANTALLA NO DECIDE NADA DE SEGURIDAD. El servidor pide el paso seguro
// (403 sin él) y aquí solo se obedece: si contesta que venció, se vuelve a
// pedir la identidad y se repite lo que se estaba haciendo, sin perder el
// nombre escrito.
//
// EL ENLACE `?m=mi-cuenta&seccion=llaves` (L-2) y la tarjeta «Entra con un
// toque» (R-LL-4) traen aquí: al montarse, el bloque se pone a la vista y
// toma el foco en su título, y el parámetro se quita de la dirección para
// que recargar no vuelva a saltar.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Fingerprint, History, KeyRound, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import { reescribirUrlEn } from '../ui/navegacion';
import ConfirmarIdentidad from './ConfirmarIdentidad';
import {
  ANCLA_LLAVES, AVISO_SECCION, SECCION_LLAVES, aparatoSinLlave, crearLlave,
  hayLlavesEnEsteAparato, pasoSeguroVigente, pedirOpcionesDeRegistro, quitarMiLlave,
  tomarSeccionPedida,
} from './llaveDeAcceso';
import { consecuenciaDeQuitar, fechaDeLlave, tipoDeLlave } from './llavesDeLaCuenta';

// Las opciones de alta traen un reto de 2 minutos: pasado este rato se
// piden otras antes de abrir la ventana del aparato.
const OPCIONES_FRESCAS_MS = 90 * 1000;

function Aviso({ tipo, children }) {
  if (!children) return null;
  const clases = {
    ok: 'text-green-800 bg-green-50 border-green-200',
    error: 'text-red-700 bg-red-50 border-red-200',
    gris: 'text-gray-700 bg-gray-50 border-gray-200',
  }[tipo];
  return (
    <p role={tipo === 'error' ? 'alert' : 'status'} data-aviso={tipo}
       className={`text-sm border rounded-lg p-2.5 ${clases}`}>{children}</p>
  );
}

function botonSecundario(extra = '') {
  return `inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${extra}`;
}

function NombreEditable({ llave, onGuardado }) {
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(llave.apodo || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const limpio = nombre.trim();
  const falta = !limpio ? 'escribe un nombre' : (limpio.length > 80 ? 'usa 80 letras o menos' : '');

  async function guardar(e) {
    e.preventDefault();
    if (falta) return;
    setGuardando(true);
    setError(null);
    try {
      const r = await api.put(`/seguridad/llaves/${llave.id}`, { apodo: limpio });
      setEditando(false);
      onGuardado(r?.mensaje || 'Nombre actualizado.');
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  }

  if (!editando) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <p className="break-words text-sm font-semibold text-gray-900" data-campo="nombre">{llave.apodo || 'Llave sin nombre'}</p>
        <button type="button" onClick={() => { setNombre(llave.apodo || ''); setEditando(true); }}
                className={botonSecundario('min-h-11 border-transparent px-2 font-medium text-[var(--color-marca)]')}>
          <Pencil className="h-4 w-4" aria-hidden="true" /> Cambiar nombre
        </button>
      </div>
    );
  }
  const idCampo = `nombre-llave-${llave.id}`;
  return (
    <form onSubmit={guardar} className="space-y-2">
      <label htmlFor={idCampo} className="block text-sm font-medium">Nombre de la llave</label>
      <input id={idCampo} value={nombre} maxLength={80} autoFocus onChange={(e) => setNombre(e.target.value)}
             className="entrada w-full" />
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={!!falta || guardando}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-[var(--color-marca)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Guardar nombre
        </button>
        <button type="button" onClick={() => setEditando(false)} className={botonSecundario()}>Cancelar</button>
        {falta && <span role="note" className="text-xs text-gray-500">Para guardar, {falta}.</span>}
      </div>
      <Aviso tipo="error">{error}</Aviso>
    </form>
  );
}

// LO QUE PASÓ CON TUS LLAVES (§1 «Bitácora»). Cada alta, entrada, intento
// fallido y baja, con el aparato y la dirección: lo mismo que guarda la
// bitácora de la empresa, visto solo por su dueño. Se despliega en su sitio
// (un acordeón, no una vista: atrás no tiene nada que deshacer) y lee la
// bitácora solo al abrirlo.
// Lo que pide revisar algo va en amarillo; lo demás, normal.
const DE_ATENCION = new Set(['LLAVE_ACCESO_FALLO', 'LLAVE_ACCESO_BLOQUEADA', 'LLAVE_IDENTIDAD_FALLO', 'LLAVE_ACCESO_QUITADA_POR_SUPREMO']);

function ActividadDeLlaves({ sello = 0 }) {
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [leidoCon, setLeidoCon] = useState(null);

  const cargar = useCallback(() => {
    setError(null);
    return api.get('/seguridad/llaves/actividad').then((d) => { setDatos(d); setLeidoCon(sello); })
      .catch((e) => setError(`No se pudo leer la actividad de tus llaves. ${e.message || ''} Pulsa «Volver a intentar».`.replace(/\s+/g, ' ')));
  }, [sello]);

  function alternar() {
    const nuevo = !abierto;
    setAbierto(nuevo);
    // Se vuelve a leer si cambió algo en la lista desde la última vez.
    if (nuevo && (!datos || leidoCon !== sello)) cargar();
  }

  const filas = datos?.actividad || [];
  return (
    <div className="rounded-lg border border-gray-200" data-bloque="actividad-de-llaves">
      <button type="button" onClick={alternar} aria-expanded={abierto} aria-controls="actividad-de-llaves"
              className="flex min-h-11 w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50">
        <span className="inline-flex items-center gap-2"><History className="h-4 w-4" aria-hidden="true" /> Lo que pasó con tus llaves</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${abierto ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {abierto && (
        <div id="actividad-de-llaves" className="space-y-2 border-t border-gray-100 p-3">
          {!datos && !error && (
            <p role="status" className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando la actividad…
            </p>
          )}
          {error && (
            <div className="space-y-2">
              <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">{error}</p>
              <button type="button" onClick={cargar}
                      className="min-h-11 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold">Volver a intentar</button>
            </div>
          )}
          {datos && filas.length === 0 && (
            <p className="text-sm text-gray-600" data-estado="vacio">{datos.vacio}</p>
          )}
          {filas.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {filas.map((f, i) => (
                <li key={`${f.fecha}-${i}`} data-accion-bitacora={f.accion} className="py-2">
                  <p className={`text-sm font-medium ${DE_ATENCION.has(f.accion) ? 'text-amber-800' : 'text-gray-900'}`}>{f.texto}</p>
                  <p className="text-xs text-gray-600">
                    {[fechaDeLlave(f.fecha), f.aparato, f.ip && `dirección ${f.ip}`].filter(Boolean).join(' · ')}
                  </p>
                  {f.motivo && <p className="text-xs text-gray-600">Motivo: {f.motivo}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function LlavesDeAcceso({ Contenedor }) {
  const [datos, setDatos] = useState(null);
  const [errorCarga, setErrorCarga] = useState(null);
  const [pideCodigo, setPideCodigo] = useState(null);
  // El alta en curso: null, o { opciones, nombre } una vez confirmada la identidad.
  const [alta, setAlta] = useState(null);
  const [trabajando, setTrabajando] = useState(null);
  const [identidad, setIdentidad] = useState(null);
  const [quitar, setQuitar] = useState(null);
  const [aviso, setAviso] = useState(null);
  const titulo = useRef(null);
  // Lo que lleva el alta en curso, leído al momento de crear (el diálogo
  // de identidad puede haberse abierto entre medias).
  const altaActual = useRef(null);
  altaActual.current = alta;
  const admite = hayLlavesEnEsteAparato();

  const cargar = useCallback(() => {
    setErrorCarga(null);
    return api.get('/seguridad/llaves').then(setDatos)
      .catch((e) => setErrorCarga(`No se pudieron leer tus llaves de acceso. ${e.message || ''} Pulsa «Volver a intentar».`.replace(/\s+/g, ' ')));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/auth/2fa/estado').then((e) => setPideCodigo(!!e?.activado)).catch(() => setPideCodigo(null));
  }, []);

  // LLEGAR AQUÍ POR EL ENLACE. Solo una vez por montaje, y la dirección se
  // limpia sin recargar.
  //
  // LA SECCIÓN SE QUEDA A LA VISTA MIENTRAS LA PÁGINA SE ACOMODA (L-4). El
  // salto ocurre al montarse, y los bloques de arriba de Mi cuenta terminan
  // de cargar después: crecen y empujan esta sección fuera de la pantalla.
  // Lo midió el recorrido de navegador: se llegaba a «Mi foto», no aquí.
  // Así que durante 2 segundos, cada vez que el alto de la página cambia, se
  // vuelve a poner arriba; en cuanto la persona desplaza o teclea, se suelta.
  const soltarAncla = useRef(null);
  const irAlBloque = useCallback(() => {
    const el = document.getElementById(ANCLA_LLAVES);
    try { el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }); } catch { /* sin desplazamiento */ }
    titulo.current?.focus({ preventScroll: true });
    soltarAncla.current?.();
    if (!el || typeof ResizeObserver !== 'function') return;
    const observador = new ResizeObserver(() => {
      try { el.scrollIntoView({ block: 'start' }); } catch { /* sin desplazamiento */ }
    });
    const eventos = ['wheel', 'touchmove', 'keydown', 'mousedown'];
    const soltar = () => {
      observador.disconnect();
      clearTimeout(plazo);
      eventos.forEach((n) => window.removeEventListener(n, soltar));
      soltarAncla.current = null;
    };
    const plazo = setTimeout(soltar, 2000);
    eventos.forEach((n) => window.addEventListener(n, soltar, { passive: true }));
    observador.observe(document.body);
    soltarAncla.current = soltar;
  }, []);
  useEffect(() => () => soltarAncla.current?.(), []);
  useEffect(() => {
    if (tomarSeccionPedida() === SECCION_LLAVES) {
      irAlBloque();
      try {
        const q = new URLSearchParams(window.location.search);
        if (q.has('seccion')) {
          q.delete('seccion');
          reescribirUrlEn(window, `${window.location.pathname}?${q.toString()}`);
        }
      } catch { /* sin historial */ }
    }
    const alPedir = (e) => { if (e?.detail?.seccion === SECCION_LLAVES) { tomarSeccionPedida(); irAlBloque(); } };
    window.addEventListener(AVISO_SECCION, alPedir);
    return () => window.removeEventListener(AVISO_SECCION, alPedir);
  }, [irAlBloque]);

  // HACER ALGO QUE PIDE EL PASO SEGURO. Si no hay uno vigente, o el
  // servidor dice que venció, se pide la identidad y se repite. Cada
  // acción atiende sus propios errores; aquí solo sube el del paso.
  const conPasoSeguro = useCallback(async (accion, avisoVencido = null) => {
    const paso = pasoSeguroVigente();
    if (!paso) { setIdentidad({ accion, aviso: avisoVencido }); return; }
    try {
      await accion(paso);
    } catch (e) {
      if (e?.codigo !== 'PASO_SEGURO') throw e;
      setIdentidad({ accion, aviso: 'Tu confirmación anterior ya venció. Escribe otra vez tus datos para seguir.' });
    }
  }, []);

  // Lo que se dice cuando el alta no termina. Cancelar la ventana del
  // aparato no es un fallo: gris, no rojo.
  function explicarFalloDeAlta(e) {
    if (aparatoSinLlave(e)) {
      return { tipo: 'gris', texto: 'No se creó la llave: la ventana de tu aparato se cerró antes de terminar. Cuando quieras, pulsa otra vez «Crear la llave».' };
    }
    if (e?.name === 'InvalidStateError') {
      return { tipo: 'gris', texto: 'Este aparato ya tiene una llave de acceso para tu cuenta y está en la lista. Si no la reconoces, quítala y vuelve a agregarla.' };
    }
    if (e?.fase === 'aparato') {
      return { tipo: 'error', texto: 'No se creó la llave porque el aparato no la aceptó. Revisa que tenga huella, Face ID o PIN configurado y vuelve a intentarlo.' };
    }
    return { tipo: 'error', texto: e?.message || 'No se creó la llave. Vuelve a intentarlo.' };
  }

  // Reenvía el error del paso seguro; lo demás se pinta aquí.
  function atender(e, pintar) {
    if (e?.codigo === 'PASO_SEGURO') throw e;
    setAviso(pintar(e));
  }

  async function pedirOpciones(paso) {
    setTrabajando('opciones');
    try {
      const opciones = await pedirOpcionesDeRegistro(paso);
      setAlta((previa) => ({ opciones, nombre: previa?.nombre ?? opciones.nombre_sugerido ?? '' }));
    } catch (e) {
      atender(e, (x) => ({ tipo: 'error', texto: `No se pudo empezar a agregar la llave. ${x.message}` }));
    } finally { setTrabajando(null); }
  }

  async function crearConPaso(paso) {
    setTrabajando('crear');
    try {
      let { opciones } = altaActual.current;
      if (!opciones.recibidas_en || Date.now() - opciones.recibidas_en > OPCIONES_FRESCAS_MS) {
        opciones = await pedirOpcionesDeRegistro(paso);
      }
      await crearLlave(opciones, altaActual.current.nombre, paso);
      setAlta(null);
      setAviso({ tipo: 'ok', texto: 'Listo: ya puedes entrar con un toque desde este aparato.' });
      await cargar();
    } catch (e) {
      atender(e, explicarFalloDeAlta);
    } finally { setTrabajando(null); }
  }

  async function quitarConPaso(llave, paso) {
    setTrabajando(`quitar-${llave.id}`);
    try {
      const r = await quitarMiLlave(llave.id, paso);
      setAviso({ tipo: 'ok', texto: r?.mensaje || 'Llave de acceso retirada.' });
      await cargar();
    } catch (e) {
      atender(e, (x) => ({ tipo: 'error', texto: `No se quitó la llave. ${x.message}` }));
    } finally { setTrabajando(null); }
  }

  function empezarAlta() {
    setAviso(null);
    conPasoSeguro(pedirOpciones);
  }

  function crear() {
    if (!altaActual.current) return;
    setAviso(null);
    conPasoSeguro(crearConPaso);
  }

  function quitarLlave(llave) {
    setAviso(null);
    return conPasoSeguro((paso) => quitarConPaso(llave, paso));
  }

  const llaves = datos?.llaves || [];
  const sinServidor = datos && datos.disponible === false;
  const motivoApagado = !admite
    ? 'Este navegador no admite llaves de acceso. Ábrelo en Chrome, Safari o Edge actualizados, en un aparato con huella, Face ID o PIN.'
    : sinServidor
      ? 'Las llaves de acceso no están activadas en este servidor. Pide a quien administra TEGO que las active.'
      : null;
  const puedeAgregar = !motivoApagado && !trabajando;

  const botonAgregar = !alta && (
    <div className="space-y-1">
      <button type="button" onClick={empezarAlta} disabled={!puedeAgregar} data-accion="agregar-llave"
              aria-describedby={motivoApagado ? 'llaves-motivo-apagado' : undefined}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-marca)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 sm:w-auto">
        {trabajando === 'opciones' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
        Agregar una llave de acceso
      </button>
      {motivoApagado && <p id="llaves-motivo-apagado" role="note" className="text-sm text-gray-600">{motivoApagado}</p>}
      <Ayuda id="seguridad.llaves.agregar" />
    </div>
  );

  const contenido = (
    <>
      {!datos && !errorCarga && (
        <p className="flex items-center gap-2 text-sm text-gray-500" role="status">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando tus llaves de acceso…
        </p>
      )}
      {errorCarga && (
        <div className="space-y-2">
          <Aviso tipo="error">{errorCarga}</Aviso>
          <button type="button" onClick={cargar} className={botonSecundario()}>Volver a intentar</button>
        </div>
      )}
      {datos && llaves.length === 0 && (
        <div className="space-y-2 rounded-lg border border-dashed border-gray-300 p-3" data-estado="vacio">
          <p className="text-sm font-medium text-gray-900">No tienes ninguna llave de acceso.</p>
          <p className="text-sm text-gray-600">Una llave te deja entrar con la huella, el Face ID o el PIN de tu aparato, sin escribir la contraseña. Se agrega una vez por aparato.</p>
        </div>
      )}
      {llaves.length > 0 && (
        <ul className="divide-y divide-gray-100" aria-label="Tus llaves de acceso">
          {llaves.map((l) => {
            const tipo = tipoDeLlave(l.adjunto);
            return (
              <li key={l.id} data-llave={l.id} className="space-y-2 py-3">
                <NombreEditable llave={l} onGuardado={(t) => { setAviso({ tipo: 'ok', texto: t }); cargar(); }} />
                <dl className="grid grid-cols-1 gap-x-4 gap-y-0.5 text-xs text-gray-600 sm:grid-cols-2">
                  <div><dt className="inline">Aparato: </dt><dd className="inline" data-campo="aparato">{l.aparato || 'Sin dato del aparato'}</dd></div>
                  {tipo && <div><dt className="inline">Tipo: </dt><dd className="inline" data-campo="tipo">{tipo}</dd></div>}
                  <div><dt className="inline">Agregada: </dt><dd className="inline" data-campo="alta">{fechaDeLlave(l.creado_en) || '—'}</dd></div>
                  <div><dt className="inline">Último uso: </dt><dd className="inline" data-campo="uso">{fechaDeLlave(l.ultimo_uso_en) || 'Nunca usada'}</dd></div>
                </dl>
                {l.bloqueada && (
                  <p role="alert" data-campo="bloqueada" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">
                    Bloqueada{l.motivo_bloqueo ? `: ${l.motivo_bloqueo}` : ''}. Ya no abre tu cuenta. Quítala y agrega otra.
                  </p>
                )}
                <button type="button" onClick={() => setQuitar(l)} disabled={!!trabajando} data-accion="quitar-llave"
                        className={botonSecundario('border-red-200 text-red-700 hover:bg-red-50')}>
                  {trabajando === `quitar-${l.id}` ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />}
                  Quitar
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {datos && botonAgregar}
      {alta && (
        <div className="space-y-2 rounded-lg border border-gray-200 p-3" data-paso="nombre">
          <label htmlFor="nombre-llave-nueva" className="block text-sm font-medium">Nombre para reconocerla</label>
          <input id="nombre-llave-nueva" value={alta.nombre} maxLength={80}
                 onChange={(e) => { const v = e.target.value; setAlta((a) => ({ ...a, nombre: v })); }}
                 className="entrada w-full" />
          <p className="text-xs text-gray-600">Así la verás en esta lista. Puedes cambiarlo después.</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={crear} disabled={trabajando === 'crear'} data-accion="crear-llave"
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[var(--color-marca)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {trabajando === 'crear' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Fingerprint className="h-4 w-4" aria-hidden="true" />}
              {trabajando === 'crear' ? 'Sigue las indicaciones de tu aparato…' : 'Crear la llave'}
            </button>
            <button type="button" onClick={() => { setAlta(null); setAviso(null); }} disabled={trabajando === 'crear'}
                    className={botonSecundario()}>Cancelar</button>
          </div>
        </div>
      )}
      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}
      {datos && <ActividadDeLlaves sello={llaves.map((l) => l.id).join(',')} />}
      {quitar && (
        <ModalConfirmar
          titulo={`Quitar «${quitar.apodo || 'llave sin nombre'}»`}
          descripcion={consecuenciaDeQuitar(quitar, llaves.length, pideCodigo)}
          extra={<Ayuda id="seguridad.llaves.quitar" />}
          peligro
          etiquetaConfirmar="Quitar la llave"
          onConfirmar={() => quitarLlave(quitar)}
          onCerrar={() => setQuitar(null)}
        />
      )}
      {identidad && (
        <ConfirmarIdentidad
          pideCodigo={pideCodigo}
          aviso={identidad.aviso}
          onCerrar={() => { setIdentidad(null); setTrabajando(null); }}
          onListo={() => {
            const { accion } = identidad;
            setIdentidad(null);
            conPasoSeguro(accion);
          }}
        />
      )}
    </>
  );

  return (
    <Contenedor id={ANCLA_LLAVES} icono={KeyRound} titulo="Llaves de acceso" tituloRef={titulo}
                descripcion="Entra con la huella, el Face ID o el PIN de tu aparato, sin escribir la contraseña.">
      {contenido}
    </Contenedor>
  );
}
